/**
 * Демонстрационные страницы для отработки attachment lifecycle.
 * При форке скелета удалить или модифицировать под конкретное приложение.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import {
  ActionPanel,
  Button,
  ConfirmModal,
  DescriptionList,
  DocumentBlock,
  ExternalPageCanvas,
  ImageWithFallback,
  MenuButton,
  PageIsland,
} from '../uikit';
import type { DocumentItem } from '../uikit';
import { IconDelete, IconEdit } from '../uikit/components/icons';
import type { Product } from '../api/products';
import { deleteProducts, findProductsByIDs } from '../api/products';
import { toUserMessage } from '../api/client';
import { fileUriToSrc } from '../api/files';
import { formatMoney } from '../utils/formatNumber';

const ErrorActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space[1]}px;
`;

const ProductTitle = styled.h2`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSizes[9]}px;
  line-height: 1.5;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const MetaListWrap = styled.div`
  margin-top: ${({ theme }) => theme.space[2]}px;
`;

const MainGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space[2]}px;

  ${({ theme }) => theme.device.layoutDesktop} {
    flex-direction: row;
    align-items: flex-start;
    gap: ${({ theme }) => theme.space[4]}px;
  }
`;

const PreviewColumn = styled.div`
  flex-shrink: 0;
`;

const BodyColumn = styled.div`
  flex: 1;
  min-width: 0;
`;

const PreviewImage = styled(ImageWithFallback)`
  display: block;
  max-width: 100%;
  width: 280px;
  max-height: 280px;
  border-radius: ${({ theme }) => theme.borderRadius}px;

  ${({ theme }) => theme.device.mobile} {
    width: 100%;
    max-width: 320px;
  }
`;

const EditButtonContent = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const LoadingText = styled.p`
  margin: ${({ theme }) => theme.space[4]}px 0 0;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSizes[5]}px;
`;

const ActionPanelAddons = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space[1]}px;
  flex-wrap: wrap;
`;

const PRODUCT_DELETE_BLOCKED =
  'Изделие используется в заказах или сменах. Удаление недоступно.';

export function ProductPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const documentItems: DocumentItem[] = useMemo(() => {
    if (!product?.documents?.length) {
      return [];
    }
    return product.documents.map((d) => ({
      file: {
        id: d.file.id,
        uri: d.file.uri,
        filename: d.file.filename,
        size: d.file.size,
        ...(d.file.description ? { description: d.file.description } : {}),
      },
    }));
  }, [product?.documents]);

  const loadProduct = useCallback(async () => {
    if (!id) {
      return;
    }

    setLoading(true);
    setLoadError(null);
    try {
      const res = await findProductsByIDs([id]);
      const p = res.items[id];
      setProduct(p ?? null);
    } catch (err) {
      setProduct(null);
      setLoadError(toUserMessage(err));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void loadProduct();
  }, [loadProduct]);

  if (loading) {
    return (
      <ExternalPageCanvas>
        <LoadingText>Загрузка…</LoadingText>
      </ExternalPageCanvas>
    );
  }

  if (!product) {
    return (
      <ExternalPageCanvas>
        <ActionPanel defaultPath="/products" />
        <PageIsland>
          <p style={{ margin: 0, color: theme.colors.text.secondary }}>
            {loadError ? loadError : 'Изделие не найдено'}
          </p>
          {loadError && (
            <ErrorActions style={{ marginTop: theme.space[2] }}>
              <Button variant="filled" color="primary" onClick={() => void loadProduct()}>
                Повторить
              </Button>
            </ErrorActions>
          )}
        </PageIsland>
      </ExternalPageCanvas>
    );
  }

  const imageUrl = product.image?.uri ? fileUriToSrc(product.image.uri) : null;

  const descriptionText = product.description?.trim() ?? '';
  const showCategory = Boolean(product.category);
  const showCost = product.costPrice != null && product.costPrice !== '';
  const showSale = product.salePrice != null && product.salePrice !== '';
  const showMetaList = Boolean(descriptionText || showCategory || showCost || showSale);

  const blockedDelete = Boolean(product.referencedInOrdersOrShifts);

  const handleDeleteConfirm = async () => {
    if (!id || blockedDelete) return;
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      await deleteProducts([id]);
      setDeleteModalOpen(false);
      navigate('/products');
    } catch (err) {
      setDeleteError(toUserMessage(err));
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <ExternalPageCanvas>
      <ActionPanel
        defaultPath="/products"
        rightAddon={
          <ActionPanelAddons>
            <Button
              variant="text"
              color="secondary"
              size="s"
              onClick={() => navigate(`/products/${id}/edit`)}
            >
              <EditButtonContent>
                <IconEdit />
                Редактировать
              </EditButtonContent>
            </Button>
            {blockedDelete ? (
              <span title={PRODUCT_DELETE_BLOCKED} style={{ display: 'inline-flex' }}>
                <MenuButton
                  size={28}
                  disabled
                  aria-label={PRODUCT_DELETE_BLOCKED}
                >
                  <IconDelete />
                </MenuButton>
              </span>
            ) : (
              <MenuButton
                size={28}
                aria-label="Удалить изделие"
                onClick={() => {
                  setDeleteError(null);
                  setDeleteModalOpen(true);
                }}
              >
                <IconDelete />
              </MenuButton>
            )}
          </ActionPanelAddons>
        }
      />
      <PageIsland id="mainInfo">
        <MainGrid>
          {imageUrl && (
            <PreviewColumn>
              <PreviewImage
                src={imageUrl}
                alt={product.image?.filename ?? ''}
                objectFit="contain"
                fallbackLabel="Не найден"
              />
            </PreviewColumn>
          )}
          <BodyColumn>
            <ProductTitle>{product.name}</ProductTitle>
            {showMetaList && (
              <MetaListWrap>
                <DescriptionList>
                  {descriptionText ? (
                    <DescriptionList.Property label="Описание" dotted primary>
                      {descriptionText}
                    </DescriptionList.Property>
                  ) : null}
                  {showCategory ? (
                    <DescriptionList.Property label="Категория" dotted primary>
                      {product.category}
                    </DescriptionList.Property>
                  ) : null}
                  {showCost ? (
                    <DescriptionList.Property label="Себестоимость, руб" dotted primary>
                      {formatMoney(product.costPrice!)}
                    </DescriptionList.Property>
                  ) : null}
                  {showSale ? (
                    <DescriptionList.Property label="Цена продажи, руб" dotted primary>
                      {formatMoney(product.salePrice!)}
                    </DescriptionList.Property>
                  ) : null}
                </DescriptionList>
              </MetaListWrap>
            )}
          </BodyColumn>
        </MainGrid>
      </PageIsland>

      {documentItems.length > 0 && (
        <PageIsland title="Документы">
          <DocumentBlock
            id="product-detail-documents"
            value={documentItems}
            onChange={() => {}}
            readOnly
          />
        </PageIsland>
      )}

      <ConfirmModal
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setDeleteError(null);
        }}
        onConfirm={() => void handleDeleteConfirm()}
        title="Удалить изделие?"
        message={
          <>
            {product ? (
              <>Вы уверены, что хотите удалить изделие «{product.name}»?</>
            ) : null}
            {deleteError ? (
              <p role="alert" style={{ marginTop: theme.space[2], color: theme.colors.error.main }}>
                {deleteError}
              </p>
            ) : null}
          </>
        }
        confirmText="Удалить"
        cancelText="Отмена"
        confirmColor="error"
        loading={deleteLoading}
      />
    </ExternalPageCanvas>
  );
}
